import { useCallback, useState } from 'react';
import useClientStatusContext from '../context/useClientStatusContext';
import { ClientStatus } from '../../../model/ClientStatus';
import { ClientResponse } from '../../../model/ClientResponse';
import { ClientStatusError } from '../../../model/ClientStatusError';
import { useSecurityContext } from '../../SecurityContext';

interface ReturnType<T> {
  wrapClient(...args): Promise<ClientResponse<T>>;
  data: T;
  status: ClientStatus;
}

type ClientCall<T> = (...args) => Promise<ClientResponse<T>>;

export default <T> (client: ClientCall<T>): ReturnType<T> => {
  const { handleStatus } = useClientStatusContext();
  const { accessToken, organizationId } = useSecurityContext();

  const [status, setStatus] = useState<ClientStatus>({
    failed: false,
    finished: false,
    generalErrors: [],
    fieldErrors: [],
    loading: false,
    responseCode: undefined,
  });
  const [data, setData] = useState<T>(null);

  const wrapClient = useCallback(
    async (...args) => {
      setStatus(prevState => ({ ...prevState, loading: true }));
      try {
        const headers = {
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          ...(organizationId && { OrganizationId: encodeURIComponent(organizationId) }),
        };
        const response = await client(...args, { headers });

        const newStatus = {
          responseCode: response.responseCode,
          loading: false,
          finished: true,
          generalErrors: [],
          fieldErrors: [],
          failed: false,
        };

        setData(response.data);
        setStatus(newStatus);
        handleStatus(newStatus);

        return Promise.resolve(response);
      } catch (e) {
        const { data: responseData, status: statusCode } = e.response;

        const generalResponseErrors: ClientStatusError[] = [];

        responseData?.generalErrors?.forEach(error => generalResponseErrors.push(
          { message: error },
        ));

        const fieldResponseErrors: ClientStatusError[] = [];

        responseData?.fieldErrors?.forEach(error => fieldResponseErrors.push(
          { message: error.message, fieldName: error.field },
        ));

        const newStatus = {
          responseCode: statusCode,
          loading: false,
          finished: true,
          generalErrors: generalResponseErrors,
          fieldErrors: fieldResponseErrors,
          failed: true,
        };

        setData(null);
        setStatus(newStatus);
        handleStatus(newStatus);

        return Promise.reject();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, handleStatus, accessToken, organizationId],
  );

  return {
    wrapClient,
    data,
    status,
  };
};
